import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Search, Navigation, MapPin, Plane, ArrowLeft, Home } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icons in React Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Component to update map center
function ChangeView({ center, zoom }) {
  const map = useMap();
  map.setView(center, zoom);
  return null;
}

// Haversine formula to calculate distance in nautical miles
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3440.065; // Earth's radius in nautical miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const FlightPlanner = () => {
  const navigate = useNavigate();
  const [source, setSource] = useState('');
  const [destination, setDestination] = useState('');
  const [sourceSuggestions, setSourceSuggestions] = useState([]);
  const [destinationSuggestions, setDestinationSuggestions] = useState([]);
  const [showSourceSuggestions, setShowSourceSuggestions] = useState(false);
  const [showDestinationSuggestions, setShowDestinationSuggestions] = useState(false);
  const [sourceCoords, setSourceCoords] = useState(null);
  const [destinationCoords, setDestinationCoords] = useState(null);
  const [distance, setDistance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mapCenter, setMapCenter] = useState([12.9716, 77.5946]); // Bangalore default
  const [mapZoom, setMapZoom] = useState(10);
  
  const sourceInputRef = useRef(null);
  const destinationInputRef = useRef(null);
  const sourceDropdownRef = useRef(null);
  const destinationDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sourceDropdownRef.current && !sourceDropdownRef.current.contains(event.target) &&
          !sourceInputRef.current.contains(event.target)) {
        setShowSourceSuggestions(false);
      }
      if (destinationDropdownRef.current && !destinationDropdownRef.current.contains(event.target) &&
          !destinationInputRef.current.contains(event.target)) {
        setShowDestinationSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch location suggestions (India only)
  const fetchSuggestions = async (query, setSuggestions) => {
    if (query.length < 3) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=IN&limit=5`
      );
      const data = await response.json();
      setSuggestions(data);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  // Debounce function
  useEffect(() => {
    const timer = setTimeout(() => {
      if (source) {
        fetchSuggestions(source, setSourceSuggestions);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [source]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (destination) {
        fetchSuggestions(destination, setDestinationSuggestions);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [destination]);

  // Geocoding function using Nominatim (India only)
  const geocodeLocation = async (location) => {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(location)}&countrycodes=IN&limit=1`
    );
    const data = await response.json();
    if (data && data.length > 0) {
      return {
        lat: parseFloat(data[0].lat),
        lon: parseFloat(data[0].lon),
        display_name: data[0].display_name
      };
    }
    throw new Error('Location not found in India');
  };

  const handleCalculate = async () => {
    setError('');
    setDistance(null);
    
    if (!source || !destination) {
      setError('Please enter both source and destination');
      return;
    }

    setLoading(true);

    try {
      // Geocode both locations
      const sourceData = await geocodeLocation(source);
      const destData = await geocodeLocation(destination);

      setSourceCoords(sourceData);
      setDestinationCoords(destData);

      // Calculate distance
      const dist = calculateDistance(
        sourceData.lat,
        sourceData.lon,
        destData.lat,
        destData.lon
      );
      setDistance(dist);

      // Center map between the two points
      const centerLat = (sourceData.lat + destData.lat) / 2;
      const centerLon = (sourceData.lon + destData.lon) / 2;
      setMapCenter([centerLat, centerLon]);
      
      // Adjust zoom based on distance
      const zoomLevel = dist < 50 ? 10 : dist < 200 ? 8 : dist < 500 ? 7 : 6;
      setMapZoom(zoomLevel);

    } catch (err) {
      setError(err.message || 'Error calculating route. Please check location names.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSource('');
    setDestination('');
    setSourceSuggestions([]);
    setDestinationSuggestions([]);
    setShowSourceSuggestions(false);
    setShowDestinationSuggestions(false);
    setSourceCoords(null);
    setDestinationCoords(null);
    setDistance(null);
    setError('');
    setMapCenter([12.9716, 77.5946]);
    setMapZoom(10);
  };

  const handleSourceSelect = (suggestion) => {
    setSource(suggestion.display_name);
    setShowSourceSuggestions(false);
    setSourceSuggestions([]);
  };

  const handleDestinationSelect = (suggestion) => {
    setDestination(suggestion.display_name);
    setShowDestinationSuggestions(false);
    setDestinationSuggestions([]);
  };

  // Calculate estimated flight time (assuming average helicopter speed of 120 knots)
  const estimatedTime = distance ? (distance / 120).toFixed(2) : null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
        <nav className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_dda025fc-cd40-4797-8c5d-d84f34dad8eb/artifacts/i3r7o5v7_HighKraft.jpeg" 
              alt="HighKraft Aviation Logo" 
              className="w-16 h-16 object-contain cursor-pointer"
              onClick={() => navigate('/')}
            />
            <div className="flex flex-col">
              <h1 
                className="text-xl md:text-2xl font-bold text-aviation-blue tracking-tight leading-none cursor-pointer"
                onClick={() => navigate('/')}
              >
                HighKraft Aviation
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <div className="h-0.5 w-6 bg-aviation-gold"></div>
                <span className="text-xs font-medium text-aviation-gold uppercase tracking-wider">Flight Planner</span>
              </div>
            </div>
          </div>

          <Button
            onClick={() => navigate('/operations')}
            variant="outline"
            className="border-2 border-aviation-gold text-aviation-gold hover:bg-aviation-gold hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </nav>
      </header>

      <div className="pt-24 pb-12 px-4">
        <div className="container mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-aviation-gold/10 border border-aviation-gold px-4 py-2 rounded-full mb-4">
            <Navigation className="w-5 h-5 text-aviation-gold" />
            <span className="text-sm font-semibold text-aviation-gold">
              Flight Route Calculator
            </span>
          </div>
          <h1 className="text-4xl font-bold text-aviation-blue mb-4">
            Flight Distance Planner
          </h1>
          <p className="text-lg text-aviation-blue/70 max-w-2xl mx-auto">
            Calculate direct air distance between any two locations in India (in nautical miles)
          </p>
        </div>

        {/* Search Form */}
        <Card className="mb-8 max-w-4xl mx-auto">
          <CardHeader>
            <CardTitle className="text-aviation-blue">
              Enter Source and Destination
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-aviation-blue">
                  Source Location
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-aviation-gold z-10" />
                  <Input
                    ref={sourceInputRef}
                    type="text"
                    placeholder="e.g., Bangalore, Karnataka"
                    value={source}
                    onChange={(e) => {
                      setSource(e.target.value);
                      setShowSourceSuggestions(true);
                    }}
                    onFocus={() => sourceSuggestions.length > 0 && setShowSourceSuggestions(true)}
                    className="pl-10"
                    onKeyPress={(e) => e.key === 'Enter' && handleCalculate()}
                  />
                  
                  {showSourceSuggestions && sourceSuggestions.length > 0 && (
                    <div 
                      ref={sourceDropdownRef}
                      className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                    >
                      {sourceSuggestions.map((suggestion, index) => (
                        <div
                          key={index}
                          onClick={() => handleSourceSelect(suggestion)}
                          className="px-4 py-3 hover:bg-aviation-blue/5 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-aviation-gold mt-1 flex-shrink-0" />
                            <div className="text-sm text-aviation-blue">
                              {suggestion.display_name}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-aviation-blue">
                  Destination Location
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-aviation-gold z-10" />
                  <Input
                    ref={destinationInputRef}
                    type="text"
                    placeholder="e.g., Mumbai, Maharashtra"
                    value={destination}
                    onChange={(e) => {
                      setDestination(e.target.value);
                      setShowDestinationSuggestions(true);
                    }}
                    onFocus={() => destinationSuggestions.length > 0 && setShowDestinationSuggestions(true)}
                    className="pl-10"
                    onKeyPress={(e) => e.key === 'Enter' && handleCalculate()}
                  />
                  
                  {showDestinationSuggestions && destinationSuggestions.length > 0 && (
                    <div 
                      ref={destinationDropdownRef}
                      className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                    >
                      {destinationSuggestions.map((suggestion, index) => (
                        <div
                          key={index}
                          onClick={() => handleDestinationSelect(suggestion)}
                          className="px-4 py-3 hover:bg-aviation-blue/5 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-aviation-gold mt-1 flex-shrink-0" />
                            <div className="text-sm text-aviation-blue">
                              {suggestion.display_name}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleCalculate}
                disabled={loading}
                className="flex-1 bg-aviation-gold hover:bg-aviation-gold-dark text-white"
              >
                <Search className="w-4 h-4 mr-2" />
                {loading ? 'Calculating...' : 'Calculate Route'}
              </Button>
              <Button
                onClick={handleReset}
                variant="outline"
                className="border-2 border-aviation-gold text-aviation-gold hover:bg-aviation-gold hover:text-white"
              >
                Reset
              </Button>
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results */}
        {distance !== null && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 max-w-4xl mx-auto">
            <Card className="border-l-4 border-aviation-gold">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-2">
                  <Plane className="w-6 h-6 text-aviation-gold" />
                  <h3 className="font-semibold text-aviation-blue">Distance</h3>
                </div>
                <p className="text-3xl font-bold text-aviation-blue">
                  {distance.toFixed(2)}
                </p>
                <p className="text-sm text-aviation-blue/70">Nautical Miles</p>
                <p className="text-xs text-aviation-blue/60 mt-1">
                  ({(distance * 1.852).toFixed(2)} km)
                </p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-aviation-gold">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-2">
                  <Navigation className="w-6 h-6 text-aviation-gold" />
                  <h3 className="font-semibold text-aviation-blue">Est. Time</h3>
                </div>
                <p className="text-3xl font-bold text-aviation-blue">
                  {estimatedTime}
                </p>
                <p className="text-sm text-aviation-blue/70">Hours</p>
                <p className="text-xs text-aviation-blue/60 mt-1">
                  (at 120 knots avg. speed)
                </p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-aviation-gold">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-2">
                  <MapPin className="w-6 h-6 text-aviation-gold" />
                  <h3 className="font-semibold text-aviation-blue">Route Type</h3>
                </div>
                <p className="text-3xl font-bold text-aviation-blue">Direct</p>
                <p className="text-sm text-aviation-blue/70">Great Circle</p>
                <p className="text-xs text-aviation-blue/60 mt-1">
                  Shortest air path
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Map */}
        <Card className="max-w-6xl mx-auto">
          <CardHeader>
            <CardTitle className="text-aviation-blue">Route Visualization</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[500px] rounded-lg overflow-hidden">
              <MapContainer
                center={mapCenter}
                zoom={mapZoom}
                style={{ height: '100%', width: '100%' }}
              >
                <ChangeView center={mapCenter} zoom={mapZoom} />
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                
                {sourceCoords && (
                  <Marker position={[sourceCoords.lat, sourceCoords.lon]}>
                    <Popup>
                      <strong>Source:</strong><br />
                      {sourceCoords.display_name}
                    </Popup>
                  </Marker>
                )}
                
                {destinationCoords && (
                  <Marker position={[destinationCoords.lat, destinationCoords.lon]}>
                    <Popup>
                      <strong>Destination:</strong><br />
                      {destinationCoords.display_name}
                    </Popup>
                  </Marker>
                )}
                
                {sourceCoords && destinationCoords && (
                  <Polyline
                    positions={[
                      [sourceCoords.lat, sourceCoords.lon],
                      [destinationCoords.lat, destinationCoords.lon]
                    ]}
                    color="#E67E22"
                    weight={3}
                    opacity={0.8}
                    dashArray="10, 10"
                  />
                )}
              </MapContainer>
            </div>

            <div className="mt-4 p-4 bg-aviation-blue/5 rounded-lg">
              <h4 className="font-semibold text-aviation-blue mb-2">How it works:</h4>
              <ul className="text-sm text-aviation-blue/70 space-y-1">
                <li>• Distance calculated using the Haversine formula (great circle distance)</li>
                <li>• Direct air route shown as dashed orange line</li>
                <li>• 1 Nautical Mile = 1.852 kilometers = 1.15078 statute miles</li>
                <li>• Estimated time based on average helicopter cruise speed (120 knots)</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
      </div>
    </div>
  );
};

export default FlightPlanner;
